import React, { useEffect, useState } from 'react';
import { MasterServiceAreaPlanPrice, MasterServiceAreaPlanPriceService } from 'us-web-services';
import { Button } from 'us-react-components/dist/Button/Button';
import { ButtonType } from 'us-react-components/dist/Button/constants';
import { colors } from 'us-react-components/dist/constants/Styles';
import toast from 'react-hot-toast';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/pro-solid-svg-icons/faGift';
import { useModal } from './modalContext';

import { useDependencies } from '../dependencies';

const MOBILE_WIDTH = 1377;
const SMALL_WIDTH = 1512;
const MEDIUM_WIDTH = 1925;

export const MembershipDiscountModal = (): JSX.Element => {
  const modal = useModal();
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [membershipDiscount, setMembershipDiscount] = useState<MasterServiceAreaPlanPrice>();
  const {
      stores: { user: userStore }
    } = useDependencies();

  const { user } = userStore;
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_WIDTH);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth >= MOBILE_WIDTH && window.innerWidth <= SMALL_WIDTH);
  const [isMediumScreen, setIsMediumScreen] = useState(window.innerWidth > SMALL_WIDTH && window.innerWidth <= MEDIUM_WIDTH);

  useEffect(() => {
    const handleResize = (): void => {
      const width = window.innerWidth;
      setIsMobile(width < MOBILE_WIDTH);
      setIsSmallScreen(width >= MOBILE_WIDTH && width <= SMALL_WIDTH);
      setIsMediumScreen(width > SMALL_WIDTH && width <= MEDIUM_WIDTH);
    };

    window.addEventListener('resize', handleResize);
    return (): void => window.removeEventListener('resize', handleResize);
  }, []);

  const closeModal = (): void => {
    modal?.setModalName(undefined);
  };

  const getModalHeight = (): string | undefined => {
    if (isMobile) {
      return undefined;
    }

    if (isSmallScreen) {
      return '500px';
    }

    return isMediumScreen ? '450px' : '550px';
  };

  const getBackgroundSize = (): string => isMediumScreen ? '100%' : 'cover';

  const getBackgroundPosition = (): string => isMediumScreen ? 'center top' : 'center center';

  const setTextInfo = (): string | undefined => {
    if (isSmallScreen) {
      return membershipDiscount?.label && membershipDiscount?.description ? 'md:mt-44' : 'md:mt-60';
    }

    return membershipDiscount?.label && membershipDiscount?.description ? 'md:mt-56' : 'md:mt-80';
  };

  useEffect(() => {
    const fetchMsaPrices = async (): Promise<void> => {
      try {
        const response = await MasterServiceAreaPlanPriceService.getByFilter({
          params: {
            userId: user.id,
            sitterId: user.id,
          }
        });

        const msaPrices = response.data.data;
        const bestDiscount = msaPrices.reduce((best, price) => {
          if (
            price.plan?.masterServiceAreaDiscount &&
            (!best?.plan?.masterServiceAreaDiscount ||
              price.plan.masterServiceAreaDiscount > best.plan.masterServiceAreaDiscount)
          ) {
            return price;
          }
          return best;
        }, undefined as MasterServiceAreaPlanPrice | undefined);

        if (bestDiscount) {
          setMembershipDiscount(bestDiscount);
        }
      } catch {
        toast.error(t('pages.sitter.buyBackgroundCheck.modal.errorMessage'));
      }
    };

    fetchMsaPrices();
  }, [user.id]);

  return (
    <div
      className='relative flex flex-col min-h-screen md:min-h-0'
      style={{
        height: getModalHeight()
      }}
      role='dialog'
      aria-labelledby='membership-discount-title'
      aria-describedby='membership-discount-description'
    >
      <div
        className='absolute inset-0 bg-cover bg-no-repeat'
        style={{
          backgroundImage: 'url(https://assets.urbansitter.com/static/img/backgrounds/'
            + `${isMobile ? 'background-membership-discount-sm.webp' : 'background-membership-discount.webp'})`,
          backgroundSize: getBackgroundSize(),
          backgroundPosition: getBackgroundPosition(),
          height: '100%',
        }}
      />

      <div className='relative flex-1 md:flex md:justify-between'>
        <div className={`${isMobile ? 'bg-white rounded-lg w-11/12 mx-auto p-5 mt-12 text-center' : 'md:bg-transparent w-3/4 mx-auto md:w-full md:mx-0 p-5 md:p-0'}`}>
          <div className={`${isMobile ? '' : 'md:flex md:justify-between h-full'}`}>
            <div className={`${isMobile ? 'mx-auto' : 'md:ml-16 md:mt-12'}`}>
              <div className='mx-auto md:mx-0'>
                <div className={`w-24 h-24 ${isMobile ? 'mx-auto flex justify-center items-center' : 'mx-auto md:mx-0 flex items-center justify-center'}`}>
                  <FontAwesomeIcon
                    icon={faGift}
                    className='text-6xl md:text-7xl'
                    style={{ color: isMobile ? colors.summer.DEFAULT : 'white' }}
                    aria-hidden='true'
                  />
                </div>
                <div className={`text-3xl md:text-4xl mt-5 md:mt-8 ${isMobile ? 'text-center' : 'md:text-left'} font-bold md:-ml-4`}>
                  <span
                    id='membership-discount-title'
                    style={{ color: isMobile ? colors.summer.DEFAULT : 'white' }}
                  >
                    {t('pages.sitter.buyBackgroundCheck.modal.surprise')}
                  </span>
                </div>
              </div>
            </div>

            <div id='membership-discount-description' className={`mt-10 ${isMobile ? 'text-center mb-12' : 'md:text-right md:mr-10 md:max-w-[300px]'} ${isMediumScreen ? 'md:mt-40' : setTextInfo()}`}>
              <div
                className='text-2xl md:text-xl font-bold'
                style={{ fontSize: isMediumScreen ? '1.5rem' : '1.875rem' }}
              >
                {membershipDiscount?.label}
              </div>
              <div
                className='text-lg font-bold'
                style={{ fontSize: isMediumScreen ? '1rem' : '1.25rem' }}
              >
                {t('pages.sitter.buyBackgroundCheck.modal.validFor')}
              </div>
              <div
                className='text-base'
                style={{ fontSize: isMediumScreen ? '1rem' : '1.25rem' }}
              >
                {membershipDiscount?.description}
              </div>
              <Button
                buttonType={ButtonType.primary}
                onClick={closeModal}
                className='w-full text-white py-2 px-4 rounded hover:bg-blue-700 mt-4'
                style={{
                  backgroundColor: colors.emerald.DEFAULT,
                  fontSize: isMediumScreen ? '0.875rem' : '1rem',
                  padding: isMediumScreen ? '0.5rem 1rem' : '0.5rem 1rem'
                }}
              >
                {t('pages.sitter.buyBackgroundCheck.modal.redeemButton')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className={`absolute w-11/12 mx-auto text-[10px] md:text-[8px] ${isMobile ? 'text-center left-1/2 -translate-x-1/2 bottom-2' : 'md:text-left md:left-4 md:translate-x-0 md:bottom-2'}`}>
        {t('pages.sitter.buyBackgroundCheck.modal.terms')}
      </div>
    </div>
  );
};

export default MembershipDiscountModal;
